import { ClearOutlined, CloseOutlined } from '@ant-design/icons';
import { Divider, Drawer, Space, Switch } from 'antd';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import { styled } from 'styled-components';
import useSound from 'use-sound';

// @ts-ignore
import WarningAudio from 'src/assets/audios/warning.wav';
import { MONITOR_WARNING_IGNORE_MINUTES } from 'src/config';
import { useWarningConfig, WatchWarningModeSelect } from 'src/features/warning';

import { useMonitor } from './monitor.provider';
import { PanelSwitchDropdown } from './panel-switch.dropdown';
import { WarningCard } from './warning-card';
import { WarningDrawDetail } from './warning-drawer-detail';
import { ContentEmpty } from './warning-drawer-empty';

type WarningDrawProps = {};

export const WarningDraw: React.FC<WarningDrawProps> = () => {
  const [{ settings, warnings, ui }, monitorDispatch] = useMonitor();
  const [{ result: warningConfig = {} }] = useWarningConfig();

  const CONTENT_WIDTH = 1062;
  const CARD_WIDTH = 490;
  const SETTING_WIDTH = 272;

  const contentWidth = settings.showDetail ? CONTENT_WIDTH : CARD_WIDTH;
  // 加上 padding
  const drawerWidth = 24 * 2 + contentWidth + (settings.showSetting ? SETTING_WIDTH : 0);

  // @ts-ignore
  const [play] = useSound(warningConfig?.soundFile || WarningAudio);

  const playWarningAudio = () => {
    if (!settings.mute) {
      play();
    }
  };

  useEffect(() => {
    playWarningAudio();
  }, [warnings]);

  return (
    <WarningDrawer
      open={ui.warningDrawerOpen}
      placement="right"
      width={drawerWidth}
      mask={false}
      getContainer={null}
      closable={false}
      zIndex={990}
    >
      <WarningsDrawerBox>
        <div className="header">
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
              <div className="title">报警卡片</div>
              <PanelSwitchDropdown setting={settings} onChange={monitorDispatch.set} />
            </div>

            <div
              className="icon-btn"
              role="button"
              onClick={() => {
                monitorDispatch.closeWarningDrawer();
              }}
            >
              <CloseOutlined style={{ fontSize: 10, opacity: 0.6 }} />
            </div>
          </Space>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <WarningsDrawerContent>
          <Content style={{ width: contentWidth }}>
            <ContentCard>
              <div className="custom-header card-header">
                <div>报警卡片</div>
                <div
                  className="icon-btn card-header-clear"
                  role="button"
                  title="清空所有预警"
                  aria-label="清空所有预警"
                  onClick={() => {
                    monitorDispatch.clearWarnings();
                  }}
                >
                  <ClearOutlined style={{ fontSize: 16 }} />
                </div>
              </div>
              <div className="card-content">
                {isEmpty(warnings[settings.watchMode]) ? (
                  <ContentEmpty title="暂无报警卡片" />
                ) : (
                  <>
                    {settings.hiddening && !isEmpty(warnings.hidden) && (
                      <div
                        className="card-number"
                        onClick={() => {
                          monitorDispatch.loadWarnings();
                        }}
                      >{`${warnings.hidden.length}个报警卡片`}</div>
                    )}
                    {warnings[settings.watchMode].map((warning) => (
                      <WarningCard
                        warning={warning}
                        key={warning.id}
                        selected={!!settings.selected && settings.selected.id === warning.id}
                        onClick={() => {
                          if (!!settings.selected && settings.selected.id === warning.id) {
                            monitorDispatch.selectWarning(null);
                          } else {
                            monitorDispatch.selectWarning(warning);
                          }
                        }}
                        onClose={() => monitorDispatch.deleteWarning(warning)}
                      />
                    ))}
                  </>
                )}
              </div>
            </ContentCard>
            {settings.showDetail && <WarningDrawDetail />}
          </Content>

          {settings.showSetting && (
            <Setting>
              <div className="custom-header">设置</div>
              <div className="setting-content">
                <div style={{ marginBottom: 16 }}>
                  <div className="title">报警类型</div>
                  <div>
                    <WatchWarningModeSelect
                      defaultValue={settings.watchMode}
                      style={{ width: 166 }}
                      onChange={(value) => monitorDispatch.set({ watchMode: value })}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div className="title">报警铃声</div>
                    <div className="desc">开启后，卡片弹出将伴随提示音铃声</div>
                  </div>
                  <div>
                    <Switch
                      defaultChecked={!settings.mute}
                      onChange={() => monitorDispatch.set({ mute: !settings.mute })}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div className="title">实时违法</div>
                    <div className="desc">{`打开后，仅展示最近${MONITOR_WARNING_IGNORE_MINUTES}分钟内的违法产生的报警。`}</div>
                  </div>
                  <div>
                    <Switch
                      defaultChecked={settings.showRecent}
                      onChange={() => monitorDispatch.set({ showRecent: !settings.showRecent })}
                    />
                  </div>
                </div>
              </div>
            </Setting>
          )}
        </WarningsDrawerContent>
      </WarningsDrawerBox>
    </WarningDrawer>
  );
};

const WarningDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 76px 0 12px !important;
    overflow-x: hidden !important;
  }
`;

const WarningsDrawerBox = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  .header {
    padding: 4px 24px;
    height: 36px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;

    .title {
      font-size: 18px;
      font-weight: 600;
    }
  }

  /* 图标式按钮：保持 16px 大小 */
  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    padding: 0;
    cursor: pointer;
    user-select: none;
    outline: none;
  }

  .handle-btn {
    width: 72px;
    font-size: 12px;
    padding: 0 12px;

    span + .anticon {
      margin-left: 4px !important;
    }
  }
`;

const WarningsDrawerContent = styled.div`
  padding: 8px 24px;
  display: flex;
  flex-grow: 1;

  .custom-header {
    height: 20px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    font-weight: 500;
  }
`;

const Content = styled.div`
  display: flex;
`;

const ContentCard = styled.div`
  width: 490px;

  .card-header {
    justify-content: space-between;

    .card-header-clear {
      margin-right: 16px;
    }
  }

  .card-content {
    height: 740px;
    padding: 16px;
    overflow-x: hidden;
    overflow-y: auto;

    border: 1px solid #e4e4e7;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .card-number {
      width: 445px;
      flex: 0 0 35px;
      margin-bottom: 10px;
      border-radius: 6px;
      background: rgba(213, 30, 30, 0.15);
      font-weight: 500;
      cursor: pointer;

      display: flex;
      justify-content: center;
      align-items: center;
    }

    .card-number:hover {
      background: rgba(213, 30, 30, 0.75);
    }
  }
`;

const Setting = styled.div`
  margin-left: 16px;
  flex: 1;

  .setting-content {
    margin-top: 8px;
  }

  .title {
    margin-bottom: 8px;
    font-weight: 500;
  }

  .desc {
    color: #71717a;
    font-size: 12px;
  }
`;
